import { useState, useRef, useEffect, useCallback } from "react";
import "./ChatBot.css";
import { URL } from "../common/constant";
import { FabIcon, CloseIcon, ResetIcon, SendIcon, ExpandIcon, CollapseIcon } from "./icons";
import ServiceCard from "./ServiceCard";
import ServiceDetailCard from "./ServiceDetailCard";
import TicketRedirectCard from "./TicketRedirectCard";
import ImageCard from "./ImageCard";

const WELCOME = "KuzuZangpo la! I'm BPC's virtual assistant. How can I help you today?";
const BPC_LOGO = `${process.env.PUBLIC_URL}/bpc.png`;
const QUICK_REPLIES = [
  "Register",
  "List All Services",
  "Contact Support",
  "All Services TAT",
];

function timestamp() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeUserMessage(text = "") {
  const cleaned = text.trim();
  if (!cleaned) return { display: "", request: "" };

  const patterns = [
    /^(tat|t\.a\.t\.?|turnaround\s+time(s)?|processing\s+time)(\s+(please|pls|now|info))?\??$/i,
    /all\s+services?\s+tat/i,
    /tat\s+for\s+all/i,
    /all\s+tat/i,
    /services?\s+tat/i,
    /list\s+tat/i,
  ];

  const wantsAllTat = patterns.some((re) => re.test(cleaned));
  if (wantsAllTat) {
    return { display: cleaned, request: "All Services TAT" };
  }

  return { display: cleaned, request: cleaned };
}

/**
 * Normalizes the images array from the API response.
 * Handles both plain string URLs and object shapes { url, title, caption }.
 * Prepends a sane base URL if the path is relative.
 */
function normalizeImages(images) {
  if (!Array.isArray(images) || images.length === 0) return null;

  const base = URL.replace(/\/api\/?$/, "");
  const toAbsolute = (src) => {
    if (!src) return "";
    if (/^https?:\/\//i.test(src)) return src;
    const normalized = src.startsWith("/") ? src : `/${src}`;
    return `${base}${normalized}`;
  };

  return images.map((img) => {
    const obj = typeof img === "object" && img !== null ? img : {};
    const src = typeof img === "string" ? img : obj.url || obj.image_url || obj.path || "";
    const fullUrl = toAbsolute(src);
    return {
      id:       obj.id,
      url:      fullUrl,
      title:    obj.title || "",
      caption:  obj.caption || "",
      type:     obj.type,
      filename: obj.filename,
    };
  });
}

function looksLikeTatMessage(text = "") {
  if (typeof text !== "string") return false;
  const hasTat = /\bTAT\b|Turnaround Time/i.test(text);
  const hasBullets = /(^|\n)\s*[*•-]\s+/.test(text);
  return hasTat && hasBullets;
}

function parseTatMessage(content = "") {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const headerLines = [];
  const tailLines = [];
  const looseItems = [];
  const sections = [];
  let currentSection = null;
  let listStarted = false;

  for (const line of lines) {
    const bulletMatch = line.match(/^([*•-])\s+(.*)$/);
    if (bulletMatch) {
      listStarted = true;
      const text = bulletMatch[2].trim();
      if (text.endsWith(":")) {
        const title = text.replace(/:\s*$/, "");
        currentSection = { title, items: [] };
        sections.push(currentSection);
      } else if (currentSection) {
        currentSection.items.push(text);
      } else {
        looseItems.push(text);
      }
      continue;
    }

    if (!listStarted) {
      headerLines.push(line);
    } else {
      tailLines.push(line);
    }
  }

  return { headerLines, sections, looseItems, tailLines };
}

function splitLabelValue(text) {
  const parts = text.split(/:\s*/);
  if (parts.length < 2) return { label: text, value: "", hasValue: false };
  const label = parts.shift();
  const value = parts.join(": ");
  return { label, value, hasValue: true };
}

function TatMessage({ content }) {
  const { headerLines, sections, looseItems, tailLines } = parseTatMessage(content);

  return (
    <div className="tat-message">
      {headerLines.map((line, i) => (
        <p key={`tat-head-${i}`}>{line}</p>
      ))}

      {sections.map((section, i) => (
        <div key={`tat-section-${i}`} className="tat-section">
          <div className="tat-section-title">{section.title}</div>
          {section.items.length > 0 && (
            <ul className="tat-list">
              {section.items.map((item, j) => {
                const { label, value, hasValue } = splitLabelValue(item);
                return (
                  <li key={`tat-item-${i}-${j}`} className={hasValue ? "tat-item" : undefined}>
                    {hasValue ? (
                      <>
                        <span className="tat-item-label">{label}</span>
                        <span className="tat-item-sep">:</span>
                        <span className="tat-item-value">{value}</span>
                      </>
                    ) : (
                      item
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}

      {looseItems.length > 0 && (
        <ul className="tat-list">
          {looseItems.map((item, i) => {
            const { label, value, hasValue } = splitLabelValue(item);
            return (
              <li key={`tat-loose-${i}`} className={hasValue ? "tat-item" : undefined}>
                {hasValue ? (
                  <>
                    <span className="tat-item-label">{label}</span>
                    <span className="tat-item-sep">:</span>
                    <span className="tat-item-value">{value}</span>
                  </>
                ) : (
                  item
                )}
              </li>
            );
          })}
        </ul>
      )}

      {tailLines.map((line, i) => (
        <p key={`tat-tail-${i}`}>{line}</p>
      ))}
    </div>
  );
}

function renderMessageContent(content) {
  if (typeof content !== "string") return content;
  if (looksLikeTatMessage(content)) return <TatMessage content={content} />;
  return content;
}

export default function ChatBot() {
  const [open,     setOpen]     = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: WELCOME, time: timestamp() },
  ]);
  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);

  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 80) + "px";
  }, []);

  const getHistory = (msgs) =>
    msgs
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-10)
      .map(({ role, content }) => ({ role, content }));

  const getHistoryWithOverride = (msgs, overrideText) => {
    const history = getHistory(msgs);
    if (!overrideText) return history;
    if (history.length === 0) return history;
    const last = history[history.length - 1];
    if (last.role === "user") {
      history[history.length - 1] = { ...last, content: overrideText };
    }
    return history;
  };

  const sendMessage = async (text) => {
    const raw = text ?? input;
    const { display, request } = normalizeUserMessage(raw);
    if (!display || loading) return;

    const t = timestamp();
    const updatedMessages = [...messages, { role: "user", content: display, time: t }];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    if (textareaRef.current) textareaRef.current.style.height = "auto";

    try {
      const res = await fetch(`${URL}/chat/chat/`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          message: request,
          history: getHistoryWithOverride(updatedMessages, request),
        }),
      });

      const data = await res.json();

      // ── Normalize images ──────────────────────────────────────────
      const images = normalizeImages(data.images);

      setMessages((prev) => [
        ...prev,
        {
          role:            "assistant",
          content:         data.reply || "Sorry, something went wrong. Please try again.",
          time:            timestamp(),
          service_cards:   data.service_cards  || null,
          ticket_redirect: data.ticket_redirect || false,
          ticket_url:      data.ticket_url      || null,
          images,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role:    "assistant",
          content: "Connection error. Please try again.",
          time:    timestamp(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReset = () => {
    setMessages([{ role: "assistant", content: WELCOME, time: timestamp() }]);
  };

  return (
    <>
      {/* FAB */}
      <button className="chat-fab" onClick={() => setOpen((o) => !o)} aria-label="Toggle chat">
        {open ? <CloseIcon size={18} /> : <FabIcon />}
      </button>

      {open && (
        <div className="chat-window" style={{ width: expanded ? "540px" : "360px" }}>

          {/* ── Header ── */}
          <div className="chat-header">
            <div className="chat-header-left">
              <div className="chat-avatar">
                <img src={BPC_LOGO} alt="BPC Logo" />
              </div>
              <div>
                <div className="chat-title">BPC Assistant</div>
                <div className="chat-subtitle">Online</div>
              </div>
            </div>

            <div className="chat-header-actions">
              <button className="icon-btn" onClick={handleReset} title="Reset chat">
                <ResetIcon />
              </button>
              <button
                className="icon-btn"
                onClick={() => setExpanded((e) => !e)}
                title={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </button>
              <button className="icon-btn close-btn" onClick={() => setOpen(false)} title="Close">
                <CloseIcon size={13} />
              </button>
            </div>
          </div>

          {/* ── Messages ── */}
          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`msg ${msg.role}`}>

                <div className="bubble">{renderMessageContent(msg.content)}</div>
                {msg.time && <div className="msg-time">{msg.time}</div>}

                {/* Service cards — single match = full detail card, multiple = compact cards */}
                {msg.role === "assistant" && msg.service_cards?.length > 0 && (
                  <div className="msg-cards">
                    {msg.service_cards.length === 1 ? (
                      <ServiceDetailCard
                        service={msg.service_cards[0]}
                        image={msg.images?.[0] || null}
                      />
                    ) : (
                      msg.service_cards.map((service) => (
                        <ServiceCard key={service.id} service={service} />
                      ))
                    )}
                  </div>
                )}

                {/* Ticket redirect card */}
                {msg.role === "assistant" && msg.ticket_redirect && msg.ticket_url && (
                  <div className="msg-cards">
                    <TicketRedirectCard ticketUrl={msg.ticket_url} />
                  </div>
                )}

                {/* Standalone image cards — only when no single-service detail card */}
                {msg.role === "assistant" &&
                  msg.images?.length > 0 &&
                  msg.service_cards?.length !== 1 && (
                    <div className="msg-cards">
                      <ImageCard images={msg.images} />
                    </div>
                  )}

              </div>
            ))}

            {loading && (
              <div className="msg assistant">
                <div className="typing-bubble">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* ── Quick Replies ── */}
          {!loading && (
            <div className="quick-replies">
              {QUICK_REPLIES.map((qr) => (
                <button key={qr} className="quick-reply-btn" onClick={() => sendMessage(qr)}>
                  {qr}
                </button>
              ))}
            </div>
          )}

          {/* ── Input ── */}
          <div className="chat-input-wrap">
            <div className="chat-input">
              <textarea
                ref={textareaRef}
                value={input}
                rows={1}
                placeholder="Type your message..."
                disabled={loading}
                onChange={(e) => { setInput(e.target.value); autoResize(); }}
                onKeyDown={handleKey}
              />
              <button
                className="send-btn"
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                title="Send"
              >
                <SendIcon />
              </button>
            </div>
            <div className="chat-footer">Powered by BPC</div>
          </div>

        </div>
      )}
    </>
  );
}
